import React, { useState, useRef, useEffect } from 'react';
import toast from 'react-hot-toast';
import { StepProgress } from '../common/StepProgress';
import { ErrorMessage } from '../common/ErrorMessage';
import { BowelMovement } from '../../types';
import { getErrorMessage } from '../../utils/errorMessages';
import { BristolIcon } from './BristolIcons';

const BRISTOL_TO_CONSISTENCY: Record<string, 'płynna' | 'miękka' | 'normalna' | 'twarda'> = {
  type1: 'twarda',
  type2: 'twarda',
  type3: 'normalna',
  type4: 'normalna',
  type5: 'miękka',
  type6: 'miękka',
  type7: 'płynna',
};

type AmountType = 'mała' | 'średnia' | 'duża';
type BloodTypeOption = 'jasna' | 'żywa' | 'ciemna' | 'wymieszana';

interface FormData {
  date: string;
  time: string;
  consistency: string;
  amount: AmountType;
  blood: number;
  bloodType: BloodTypeOption;
  mucus: number;
  pain: number;
  urgency: number;
  gas: number;
  notes: string;
}

const URGENCY_LABELS: Record<number, string> = {
  1: 'Brak pilności',
  2: 'Muszę iść w ciągu ~15 min',
  3: 'Duża pilność',
  4: 'Sporadyczne problemy z trzymaniem',
  5: 'Częste problemy z trzymaniem',
};

interface AddBowelMovementFormProps {
  onSubmit: (data: BowelMovement) => void | Promise<void>;
}

const consistencyOptions = [
  { value: 'type1', type: 1, desc: 'Twarde grudki' },
  { value: 'type2', type: 2, desc: 'Kształt kiełbasy' },
  { value: 'type3', type: 3, desc: 'Pęknięta' },
  { value: 'type4', type: 4, desc: 'Gładka' },
  { value: 'type5', type: 5, desc: 'Miękkie grudki' },
  { value: 'type6', type: 6, desc: 'Puszysty' },
  { value: 'type7', type: 7, desc: 'Płynny' },
];

export const AddBowelMovementForm: React.FC<AddBowelMovementFormProps> = ({ onSubmit }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [showConsistencyModal, setShowConsistencyModal] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const [formData, setFormData] = useState<FormData>({
    date: new Date().toISOString().split('T')[0],
    time: new Date().toTimeString().slice(0, 5),
    consistency: '',
    amount: 'średnia',
    blood: 0,
    bloodType: 'jasna',
    mucus: 0,
    pain: 0,
    urgency: 1,
    gas: 0,
    notes: '',
  });

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [formData.notes]);

  const getConsistencyLabel = (value: string) => {
    const option = consistencyOptions.find((o) => o.value === value);
    return option ? option.desc : 'Wybierz konsystencję';
  };

  const validateStep1 = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.date) newErrors.date = 'Wymagane';
    if (!formData.time) newErrors.time = 'Wymagane';
    if (!formData.consistency) newErrors.consistency = 'Wybierz';
    if (formData.date && new Date(formData.date) > new Date()) {
      newErrors.date = 'Z przyszłości';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = () => true;
  const validateStep3 = () => true;

  const handleNext = () => {
    let isValid = false;
    if (currentStep === 1) isValid = validateStep1();
    else if (currentStep === 2) isValid = validateStep2();
    else if (currentStep === 3) isValid = validateStep3();

    if (isValid) {
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentStep(currentStep + 1);
        setIsTransitioning(false);
      }, 150);
    }
  };

  const handleBack = () => {
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentStep(currentStep - 1);
      setErrors({});
      setIsTransitioning(false);
    }, 150);
  };

  const buildBowelMovement = (): BowelMovement => {
    const consistency =
      formData.consistency && BRISTOL_TO_CONSISTENCY[formData.consistency]
        ? BRISTOL_TO_CONSISTENCY[formData.consistency]
        : 'normalna';
    const hasBlood = formData.blood > 0;
    const hasMucus = formData.mucus > 0;
    const hasGas = formData.gas > 0;

    return {
      id: Date.now().toString(),
      date: formData.date,
      time: formData.time,
      consistency,
      hasBlood,
      bloodAmount: hasBlood ? formData.blood : undefined,
      bloodType: hasBlood ? formData.bloodType : undefined,
      hasMucus,
      mucusAmount: hasMucus ? formData.mucus : undefined,
      pain: formData.pain,
      amount: formData.amount,
      hasGas,
      gasIntensity: hasGas ? formData.gas : undefined,
      urgency: formData.urgency >= 1 ? formData.urgency : 1,
      otherSymptoms: formData.notes || '',
    };
  };

  const handleSubmit = async () => {
    try {
      const loadingToast = toast.loading('Zapisywanie...');
      const data = buildBowelMovement();
      await onSubmit(data);
      toast.success('Wypróżnienie dodane', { id: loadingToast });
      setFormData({
        date: new Date().toISOString().split('T')[0],
        time: new Date().toTimeString().slice(0, 5),
        consistency: '',
        amount: 'średnia',
        blood: 0,
        bloodType: 'jasna',
        mucus: 0,
        pain: 0,
        urgency: 1,
        gas: 0,
        notes: '',
      });
      setCurrentStep(1);
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const Slider = ({
    label,
    value,
    onChange,
  }: {
    label: string;
    value: number;
    onChange: (val: number) => void;
  }) => (
    <div style={{ marginBottom: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
        <label style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>{label}</label>
        <span style={{ fontSize: 13, fontWeight: 600, color: '#6B7FFF' }}>{value}/5</span>
      </div>
      <input
        type="range"
        min={0}
        max={5}
        value={value}
        onChange={(e) => onChange(parseInt(e.target.value, 10))}
        style={{
          width: '100%',
          height: 6,
          borderRadius: 3,
          outline: 'none',
          cursor: 'pointer',
          accentColor: '#6B7FFF',
        }}
      />
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
        {[0, 1, 2, 3, 4, 5].map((num) => (
          <span
            key={num}
            style={{
              fontSize: 9,
              color: value === num ? '#6B7FFF' : '#cbd5e1',
              fontWeight: value === num ? 600 : 400,
            }}
          >
            {num}
          </span>
        ))}
      </div>
    </div>
  );

  return (
    <>
      <div
        style={{
          background: 'white',
          borderRadius: 16,
          padding: 20,
          border: '1px solid #e2e8f0',
        }}
      >
        <h2
          style={{
            fontSize: 20,
            fontWeight: 700,
            color: '#0f172a',
            margin: '0 0 16px',
            textAlign: 'center',
          }}
        >
          Dodaj wypróżnienie
        </h2>

        <StepProgress currentStep={currentStep} totalSteps={3} />

        <div className={isTransitioning ? 'step-exit' : 'step-enter'}>
          {currentStep === 1 && (
            <div>
              <h3
                style={{
                  fontSize: 15,
                  fontWeight: 600,
                  color: '#0f172a',
                  marginBottom: 14,
                  textAlign: 'center',
                }}
              >
                Podstawowe informacje
              </h3>

              <div style={{ marginBottom: 12 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#0f172a', marginBottom: 4 }}>
                  Data *
                </label>
                <input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  max={new Date().toISOString().split('T')[0]}
                  style={{
                    width: '100%',
                    padding: 10,
                    fontSize: 14,
                    borderRadius: 8,
                    border: errors.date ? '2px solid #ef4444' : '2px solid #e2e8f0',
                    outline: 'none',
                  }}
                />
                {errors.date && <ErrorMessage message={errors.date} type="inline" />}
              </div>

              <div style={{ marginBottom: 12 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#0f172a', marginBottom: 4 }}>
                  Czas *
                </label>
                <input
                  type="time"
                  value={formData.time}
                  onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                  style={{
                    width: '100%',
                    padding: 10,
                    fontSize: 14,
                    borderRadius: 8,
                    border: errors.time ? '2px solid #ef4444' : '2px solid #e2e8f0',
                    outline: 'none',
                  }}
                />
                {errors.time && <ErrorMessage message={errors.time} type="inline" />}
              </div>

              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#0f172a', marginBottom: 4 }}>
                  Konsystencja (Bristol) *
                </label>
                <button
                  type="button"
                  onClick={() => setShowConsistencyModal(true)}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    fontSize: 14,
                    borderRadius: 8,
                    border: errors.consistency ? '2px solid #ef4444' : '2px solid #e2e8f0',
                    background: 'white',
                    textAlign: 'left',
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    color: formData.consistency ? '#0f172a' : '#94a3b8',
                  }}
                >
                  <span style={{ fontSize: 13 }}>{getConsistencyLabel(formData.consistency)}</span>
                  <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </button>
                {errors.consistency && <ErrorMessage message={errors.consistency} type="inline" />}
              </div>

              <div style={{ marginTop: 12 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#0f172a', marginBottom: 4 }}>
                  Ilość
                </label>
                <select
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value as AmountType })}
                  style={{
                    width: '100%',
                    padding: 10,
                    fontSize: 14,
                    borderRadius: 8,
                    border: '2px solid #e2e8f0',
                    outline: 'none',
                    background: 'white',
                    cursor: 'pointer',
                  }}
                >
                  <option value="mała">Mała</option>
                  <option value="średnia">Średnia</option>
                  <option value="duża">Duża</option>
                </select>
              </div>
            </div>
          )}

          {currentStep === 2 && (
            <div>
              <h3
                style={{
                  fontSize: 15,
                  fontWeight: 600,
                  color: '#0f172a',
                  marginBottom: 14,
                  textAlign: 'center',
                }}
              >
                Objawy (1/2)
              </h3>
              <Slider label="Krew" value={formData.blood} onChange={(val) => setFormData({ ...formData, blood: val })} />
              {formData.blood > 0 && (
                <div style={{ marginBottom: 14 }}>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#0f172a', marginBottom: 4 }}>
                    Rodzaj krwi
                  </label>
                  <select
                    value={formData.bloodType}
                    onChange={(e) => setFormData({ ...formData, bloodType: e.target.value as BloodTypeOption })}
                    style={{
                      width: '100%',
                      padding: 10,
                      fontSize: 14,
                      borderRadius: 8,
                      border: '2px solid #e2e8f0',
                      outline: 'none',
                      background: 'white',
                      cursor: 'pointer',
                    }}
                  >
                    <option value="jasna">Jasna</option>
                    <option value="żywa">Żywa</option>
                    <option value="ciemna">Ciemna</option>
                    <option value="wymieszana">Wymieszana</option>
                  </select>
                </div>
              )}
              <Slider label="Śluz" value={formData.mucus} onChange={(val) => setFormData({ ...formData, mucus: val })} />
              <Slider label="Ból" value={formData.pain} onChange={(val) => setFormData({ ...formData, pain: val })} />
            </div>
          )}

          {currentStep === 3 && (
            <div>
              <h3
                style={{
                  fontSize: 15,
                  fontWeight: 600,
                  color: '#0f172a',
                  marginBottom: 14,
                  textAlign: 'center',
                }}
              >
                Objawy (2/2)
              </h3>
              <div style={{ marginBottom: 14 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                  <label style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>Pilność</label>
                  <span style={{ fontSize: 13, fontWeight: 600, color: '#6B7FFF' }}>{formData.urgency}/5</span>
                </div>
                <input
                  type="range"
                  min={1}
                  max={5}
                  value={formData.urgency}
                  onChange={(e) => setFormData({ ...formData, urgency: parseInt(e.target.value, 10) })}
                  style={{
                    width: '100%',
                    height: 6,
                    borderRadius: 3,
                    outline: 'none',
                    cursor: 'pointer',
                    accentColor: '#6B7FFF',
                  }}
                />
                <div style={{ marginTop: 6, fontSize: 11, color: '#64748b', lineHeight: 1.3 }}>
                  {URGENCY_LABELS[formData.urgency] ?? URGENCY_LABELS[1]}
                </div>
              </div>
              <Slider label="Gazy" value={formData.gas} onChange={(val) => setFormData({ ...formData, gas: val })} />

              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#0f172a', marginBottom: 4 }}>
                  Notatki
                </label>
                <textarea
                  ref={textareaRef}
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Dodatkowe uwagi..."
                  rows={1}
                  style={{
                    width: '100%',
                    padding: 8,
                    fontSize: 13,
                    borderRadius: 8,
                    border: '2px solid #e2e8f0',
                    outline: 'none',
                    resize: 'none',
                    fontFamily: 'inherit',
                    overflow: 'hidden',
                    minHeight: 38,
                  }}
                />
              </div>
            </div>
          )}
        </div>

        <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
          {currentStep > 1 && (
            <button
              onClick={handleBack}
              disabled={isTransitioning}
              style={{
                flex: 1,
                padding: 12,
                borderRadius: 8,
                border: '2px solid #e2e8f0',
                background: 'white',
                color: '#64748b',
                fontSize: 14,
                fontWeight: 600,
                cursor: isTransitioning ? 'not-allowed' : 'pointer',
                opacity: isTransitioning ? 0.5 : 1,
              }}
            >
              ← Wstecz
            </button>
          )}

          <button
            onClick={currentStep === 3 ? handleSubmit : handleNext}
            disabled={isTransitioning}
            style={{
              flex: currentStep === 1 ? 1 : 2,
              padding: 12,
              borderRadius: 8,
              border: 'none',
              background: isTransitioning ? '#94a3b8' : 'linear-gradient(135deg, #6B7FFF 0%, #5A6EE6 100%)',
              color: 'white',
              fontSize: 14,
              fontWeight: 600,
              cursor: isTransitioning ? 'not-allowed' : 'pointer',
              boxShadow: isTransitioning ? 'none' : '0 4px 12px rgba(107, 127, 255, 0.3)',
            }}
          >
            {currentStep === 3 ? 'Zapisz ✓' : 'Dalej →'}
          </button>
        </div>
      </div>

      {showConsistencyModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 20,
          }}
          onClick={() => setShowConsistencyModal(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'white',
              borderRadius: 16,
              padding: 20,
              maxWidth: 450,
              width: '100%',
              maxHeight: '70vh',
              overflow: 'auto',
            }}
          >
            <h3
              style={{
                fontSize: 16,
                fontWeight: 600,
                color: '#0f172a',
                marginBottom: 14,
                textAlign: 'center',
              }}
            >
              Wybierz konsystencję (Bristol)
            </h3>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: 12,
              }}
            >
              {consistencyOptions.map((option) => (
                <button
                  key={option.value}
                  onClick={() => {
                    setFormData({ ...formData, consistency: option.value });
                    setShowConsistencyModal(false);
                    setErrors((prev) => ({ ...prev, consistency: '' }));
                  }}
                  style={{
                    padding: 16,
                    borderRadius: 12,
                    border:
                      formData.consistency === option.value
                        ? '2px solid #6B7FFF'
                        : '2px solid #e2e8f0',
                    background: formData.consistency === option.value ? '#f0f4ff' : 'white',
                    cursor: 'pointer',
                    textAlign: 'center',
                    transition: 'all 0.2s',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 8,
                  }}
                >
                  <div
                    style={{
                      width: 60,
                      height: 60,
                      background: 'white',
                      borderRadius: 8,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <BristolIcon type={option.type} />
                  </div>
                  <div
                    style={{
                      fontSize: 14,
                      fontWeight: 600,
                      color: formData.consistency === option.value ? '#6B7FFF' : '#0f172a',
                    }}
                  >
                    {option.desc}
                  </div>
                </button>
              ))}
            </div>

            <button
              onClick={() => setShowConsistencyModal(false)}
              style={{
                width: '100%',
                marginTop: 12,
                padding: 10,
                borderRadius: 8,
                border: '2px solid #e2e8f0',
                background: 'white',
                fontSize: 14,
                fontWeight: 600,
                color: '#64748b',
                cursor: 'pointer',
              }}
            >
              Anuluj
            </button>
          </div>
        </div>
      )}
    </>
  );
};
